(() => {
  "use strict";

  const $ = (root, selector) => root.querySelector(selector);
  const $$ = (root, selector) => [...root.querySelectorAll(selector)];

  function groupSizes(config) {
    const [width,height,depth] = window.SpeedcubingPuzzleDiagrams.parseDimensions(config.dimensions,config.size);
    if (config.puzzle === "cube") return config.diagram === "last-layer" ? [width*depth,width,depth,width,depth] : [width*depth,width*height,depth*height];
    if (config.puzzle === "megaminx") return config.diagram === "last-layer" ? [11, 3, 3, 3, 3, 3] : [11, 11, 11, 11, 11, 11];
    if (config.puzzle === "skewb") return [5, 5, 5];
    if (config.puzzle === "pyraminx") return config.diagram === "two-face" ? [9, 9] : [9, 9, 9];
    return [9, 9, 9, 9];
  }

  function groupLabels(config) {
    if (config.puzzle === "cube") {
      if (config.diagram === "last-layer") return ["Upper face", "Back side", "Right side", "Front side", "Left side"];
      return ["Top face", "Front face", config.view === "left" ? "Left face" : "Right face"];
    }
    if (config.puzzle === "megaminx") return config.diagram === "last-layer" ? ["Last-layer face", "Adjacent side 1", "Adjacent side 2", "Adjacent side 3", "Adjacent side 4", "Adjacent side 5"] : ["Front face", "Surrounding face 1", "Surrounding face 2", "Surrounding face 3", "Surrounding face 4", "Surrounding face 5"];
    if (config.puzzle === "skewb") return ["Top face", "Front face", config.view === "left" ? "Left face" : "Right face"];
    if (config.puzzle === "pyraminx") return config.diagram === "two-face" ? ["Left visible face", "Right visible face"] : ["Front face", "Side face", "Lower face"];
    return ["Top face", "Right face", "Bottom face", "Left face"];
  }

  function fillGroup(state, config, groupIndex, paint, api) {
    const sizes = groupSizes(config), expected = sizes.reduce((total, length) => total + length, 0), index = Number(groupIndex);
    const normalised = api.normaliseState(state), source = normalised.length === expected ? normalised : api.defaultState(config);
    if (!Number.isInteger(index) || index < 0 || index >= sizes.length) return source;
    const offset = sizes.slice(0, index).reduce((total, length) => total + length, 0);
    return source.slice(0, offset) + paint.repeat(sizes[index]) + source.slice(offset + sizes[index]);
  }

  function grouped(state, config) {
    let offset = 0;
    return groupSizes(config).map((length) => {
      const part = state.slice(offset, offset + length); offset += length; return part;
    }).join("\n");
  }

  function downloadBlob(blob, filename) {
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob); link.download = filename; link.click();
    setTimeout(() => URL.revokeObjectURL(link.href), 1000);
  }

  function initialise(root) {
    const api = window.SpeedcubingPuzzleDiagrams;
    if (!api) return;
    const puzzle = $(root, "[data-pd-puzzle]"), diagram = $(root, "[data-pd-diagram]"), size = $(root, "[data-pd-size]"), dimensions = $(root, "[data-pd-dimensions]");
    const view = $(root, "[data-pd-view]"), gap = $(root, "[data-pd-gap]"), stateInput = $(root, "[data-pd-state]");
    const preview = $(root, "[data-pd-preview]"), status = $(root, "[data-pd-status]"), hint = $(root, "[data-pd-hint]");
    const diagramField = $(root, "[data-pd-diagram-field]"), sizeField = $(root, "[data-pd-size-field]"), dimensionsField = $(root, "[data-pd-dimensions-field]"), viewField = $(root, "[data-pd-view-field]");
    const embedBox = $(root, "[data-pd-embed]"), faceSelect = $(root, "[data-pd-face]");
    let paint = "y";

    const validDimensions = () => !dimensions.value.trim() || /^(\d+)\s*[x×]\s*(\d+)\s*[x×]\s*(\d+)$/.test(dimensions.value.trim());
    const config = () => ({ puzzle: puzzle.value, diagram: puzzle.value === "cube" ? diagram.value : "three-d", size: Number(size.value), dimensions: dimensions.value.trim(), view: view.value, gap: Number(gap.value), state: api.normaliseState(stateInput.value) });
    const labels = () => {
      if (puzzle.value === "cube") return diagram.value === "last-layer" ? "upper face, back, right, front and left strips" : "top, front and side";
      return { pyraminx: "front, side and lower visible faces, from the top down", skewb: "top, front and side", megaminx: "front face, then the five complete surrounding faces clockwise", fto: "top, right, bottom and left triangular faces" }[puzzle.value];
    };

    function updateVisibility() {
      const cube = puzzle.value === "cube", angled = (cube && diagram.value === "three-d") || puzzle.value === "skewb" || puzzle.value === "pyraminx";
      diagramField.hidden = !cube; sizeField.hidden = !cube; dimensionsField.hidden = !cube; viewField.hidden = !angled;
      hint.textContent = `Order: ${labels()}. Grey (x) marks an irrelevant sticker. Spaces and line breaks are ignored.`;
    }

    function updateFaceOptions() {
      const previous = faceSelect.value, names = groupLabels(config());
      faceSelect.replaceChildren(...names.map((name, index) => Object.assign(document.createElement("option"), { value: String(index), textContent: name })));
      if (names[Number(previous)]) faceSelect.value = previous;
    }

    function render() {
      const cfg = config(), expected = api.expectedCount(cfg), actual = cfg.state.length;
      Object.entries(cfg).forEach(([key, value]) => preview.dataset[key] = value);
      if (cfg.puzzle === "cube" && !validDimensions()) {
        preview.replaceChildren(); status.textContent = "Use dimensions such as 3x2x3."; status.className = "pd-generator__status pd-generator__status--bad"; return;
      }
      if (actual !== expected) {
        preview.replaceChildren();
        status.textContent = `Enter ${expected} colours — currently ${actual}.`;
        status.className = "pd-generator__status pd-generator__status--bad";
        return;
      }
      status.textContent = `Valid diagram · ${expected} visible pieces`;
      status.className = "pd-generator__status pd-generator__status--good";
      api.renderElement(preview, (index) => {
        const chars = api.normaliseState(stateInput.value).split(""); chars[index] = paint;
        stateInput.value = grouped(chars.join(""), config()); render();
      });
    }

    function reset() {
      const cfg = config(); stateInput.value = grouped(api.defaultState(cfg), cfg); updateVisibility(); updateFaceOptions(); render();
    }

    puzzle.addEventListener("change", reset); diagram.addEventListener("change", reset); size.addEventListener("change", reset); dimensions.addEventListener("change", reset);
    view.addEventListener("change", () => { updateFaceOptions(); render(); }); gap.addEventListener("input", render); stateInput.addEventListener("input", render);
    $$(root, "[data-colour]").forEach((button) => button.addEventListener("click", () => {
      $$(root, "[data-colour]").forEach((item) => item.classList.remove("is-active"));
      button.classList.add("is-active"); paint = button.dataset.colour;
    }));
    $(root, "[data-pd-reset]").addEventListener("click", reset);
    $(root, "[data-pd-fill-all]").addEventListener("click", () => {
      const cfg = config(), length = api.expectedCount(cfg);
      stateInput.value = grouped(paint.repeat(length), cfg); render();
    });
    $(root, "[data-pd-fill-face]").addEventListener("click", () => {
      const cfg = config();
      stateInput.value = grouped(fillGroup(stateInput.value, cfg, faceSelect.value, paint, api), cfg); render();
    });
    $(root, "[data-pd-random]").addEventListener("click", () => {
      const cfg = config(), chars = Object.keys(api.colours), length = api.expectedCount(cfg);
      stateInput.value = grouped(Array.from({ length }, () => chars[Math.floor(Math.random() * chars.length)]).join(""), cfg); render();
    });
    $(root, "[data-pd-copy]").addEventListener("click", async (event) => {
      const cfg = config();
      const geometry = cfg.dimensions ? `data-dimensions="${cfg.dimensions}"` : `data-size="${cfg.size}"`;
      const code = `<div class="puzzle-diagram" data-puzzle="${cfg.puzzle}" data-diagram="${cfg.diagram}" ${geometry} data-view="${cfg.view}" data-state="${cfg.state}"></div>`;
      embedBox.textContent = code; embedBox.hidden = false;
      try { await navigator.clipboard.writeText(code); const old = event.currentTarget.textContent; event.currentTarget.textContent = "Copied!"; setTimeout(() => event.currentTarget.textContent = old, 1300); } catch (_) {}
    });
    $(root, "[data-pd-svg]").addEventListener("click", () => {
      const drawing = $(preview, "svg"); if (!drawing) return;
      const clone = drawing.cloneNode(true); clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
      downloadBlob(new Blob([new XMLSerializer().serializeToString(clone)], { type: "image/svg+xml" }), `${puzzle.value}-diagram.svg`);
    });
    $(root, "[data-pd-png]").addEventListener("click", () => {
      const drawing = $(preview, "svg"); if (!drawing) return;
      const clone = drawing.cloneNode(true); clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
      const source = new XMLSerializer().serializeToString(clone), image = new Image(), canvas = document.createElement("canvas");
      canvas.width = 1000; canvas.height = 1100;
      image.onload = () => { const ctx = canvas.getContext("2d"); ctx.clearRect(0,0,canvas.width,canvas.height); ctx.drawImage(image,0,0,canvas.width,canvas.height); canvas.toBlob((blob) => blob && downloadBlob(blob,`${puzzle.value}-diagram.png`),"image/png"); URL.revokeObjectURL(image.src); };
      image.src = URL.createObjectURL(new Blob([source], { type: "image/svg+xml" }));
    });

    updateVisibility(); updateFaceOptions(); render();
  }

  function initialiseAll(root = document, attempt = 0) {
    if (!window.SpeedcubingPuzzleDiagrams) {
      if (attempt < 100) setTimeout(() => initialiseAll(root, attempt + 1), 50);
      return;
    }
    root.querySelectorAll("[data-puzzle-generator]").forEach((generator) => {
      if (!generator.dataset.pdInitialised) {
        generator.dataset.pdInitialised = "true";
        initialise(generator);
      }
    });
  }
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", () => initialiseAll()); else initialiseAll();
  document.addEventListener("shopify:section:load", (event) => initialiseAll(event.target));
  window.SpeedcubingPuzzleGenerator = { groupSizes, groupLabels, fillGroup };
})();
